import { useCallback, useRef, Dispatch, SetStateAction } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { getRefreshToken, StartSessionPayload } from 'redux/actions/ai-role-play';
import { wrapThunkAction } from 'redux/utils';

import { SessionData } from '../ai-conversation-modal/context';

// Refresh the token if it expires within this buffer (in ms)
const TOKEN_EXPIRY_BUFFER_MS = 60 * 1000; // 1 minute

interface UseTokenRefreshParams {
  sessionData: SessionData;
  setSessionData: Dispatch<SetStateAction<SessionData>>;
}

/**
 * Hook that provides a function to ensure the JWT token is fresh
 * before making API calls. If the token is expired or about to expire,
 * it will call the refresh endpoint and update sessionData.
 *
 * Returns a function that resolves with the valid token string,
 * or rejects if refresh fails.
 */
const useTokenRefresh = ({ sessionData, setSessionData }: UseTokenRefreshParams) => {
  const dispatch = useDispatch();
  const currentCatalogId = useSelector((state) => state.app.currentCatalogId);

  // Ref avoids stale closures when ensureFreshToken is called across async turns
  const sessionDataRef = useRef<SessionData>(sessionData);
  sessionDataRef.current = sessionData;

  /**
   * Ensures the token is valid. If expired/expiring soon, refreshes it.
   * Returns the valid token to use for the next API call.
   */
  const ensureFreshToken = useCallback(async (): Promise<string> => {
    const { jwtToken, aiApiEndpoint, submissionId, expiresAt } = sessionDataRef.current;

    if (!jwtToken || !aiApiEndpoint) {
      throw new Error('No active session');
    }

    const isExpiring = expiresAt
      && Date.now() >= new Date(expiresAt).getTime() - TOKEN_EXPIRY_BUFFER_MS;

    if (!isExpiring) return jwtToken;

    if (!submissionId) {
      throw new Error('No submission ID available for token refresh');
    }

    const result = await wrapThunkAction(
      dispatch(getRefreshToken({
        submissionId,
        catalogId: currentCatalogId,
      })),
    );

    const payload = result.payload as StartSessionPayload;
    const newToken = payload?.jwtToken;
    const newExpiresAt = payload?.expiresAt || null;

    if (!newToken) {
      throw new Error('Token refresh failed');
    }

    setSessionData((prev) => ({
      ...prev,
      jwtToken: newToken,
      expiresAt: newExpiresAt,
    }));

    return newToken;
  }, [dispatch, currentCatalogId, setSessionData]);

  return { ensureFreshToken };
};

export default useTokenRefresh;
